/**
 * PurchaseConfirmationSheet — Platform-native purchase confirmation overlay.
 *
 * Rendered by PluginRenderer when a plugin requests a purchase via store_purchase.
 * Shows product details, price, and user's credit balance. User must confirm
 * before the platform charges credits. Mirrors Apple's App Store purchase sheet.
 */

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2, ShoppingCart, AlertCircle } from 'lucide-react';

interface PurchaseProduct {
  productIdentifier: string;
  name: string;
  description: string | null;
  priceCredits: number;
  type: string;
}

interface PurchaseConfirmationSheetProps {
  open: boolean;
  product: PurchaseProduct | null;
  userBalance: number | null;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function PurchaseConfirmationSheet({
  open,
  product,
  userBalance,
  loading,
  onConfirm,
  onCancel,
}: PurchaseConfirmationSheetProps) {
  if (!product) return null;

  const insufficient = userBalance !== null && userBalance < product.priceCredits;
  const balanceUnknown = userBalance === null;
  const isFree = product.priceCredits === 0;
  const priceDisplay = isFree ? 'Free' : `${product.priceCredits} credits`;
  const dollarDisplay = `$${(product.priceCredits / 100).toFixed(2)}`;

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent className="max-w-sm">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Confirm Purchase
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <div className="rounded-lg border p-3">
                <p className="font-medium text-foreground">{product.name}</p>
                {product.description && (
                  <p className="text-sm text-muted-foreground mt-1">{product.description}</p>
                )}
                <p className="text-sm font-medium text-foreground mt-2">
                  {isFree ? 'Free' : `${priceDisplay} (${dollarDisplay})`}
                </p>
              </div>
              {userBalance !== null && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Your balance</span>
                  <span className={insufficient ? 'text-destructive font-medium' : ''}>
                    {userBalance} credits
                  </span>
                </div>
              )}
              {insufficient && (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                  <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                  <p className="text-sm text-destructive">
                    Insufficient credits. You need {product.priceCredits - (userBalance ?? 0)} more
                    credits to make this purchase.
                  </p>
                </div>
              )}
              {balanceUnknown && !isFree && (
                <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-3">
                  <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Unable to load your credit balance. The purchase may fail if you have
                    insufficient credits.
                  </p>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={loading || insufficient}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : isFree ? (
              'Get for free'
            ) : (
              `Buy for ${priceDisplay}`
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
